import React from 'react';
import { IProcessManager } from '../../Interfaces/IProcessManager';
import { IProcessManagerAutomation } from '../../Interfaces/IProcessManagerAutomation';
import { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useGetProcessManagerAutomationTypes } from '../../hooks/reactQuerys/ProcessManagerQueries';
import { PMAutomationsRow } from './PMAutomationsRow';
import { AddPMAutomation } from './AddPMAutomation';
import { tableCellClasses } from '@mui/material/TableCell';
import { ILegalClient } from '../../Interfaces/ILegalClient';

interface IProps {
  processManager: IProcessManager;
  automations: IProcessManagerAutomation[];
  setAutomations: Function;
  //
  legalClients: ILegalClient[];
  legalPhases: ILegalPhaseFullObject[];
  legalReqStates: ILegalRequirementState[];
  //
  setIsExpantedAutomations?: Function;
  /**
   * Nur Automationen anzeigen, deren ``data.Trigger`` exakt diesem Wert
   * entspricht. Wird vom HBCI-Tracker genutzt, um zwei separate Buckets
   * fuer „balance-change" und „target-reached" zu rendern. ``undefined``
   * = kein Filter (Default, alle anderen PM-Types).
   */
  triggerFilter?: string;
  /**
   * Beim Anlegen einer neuen Automation wird ``data.Trigger`` auf diesen
   * Wert vorbelegt — passt zum aktuellen Bucket.
   */
  defaultTrigger?: string;
  /**
   * Wenn ``true``, wird der Add-Button + die Delete-Buttons der Rows
   * ausgeblendet. Wird vom HBCI-Tracker gesetzt, sobald die Zielsumme
   * erreicht wurde — der Eintrag bleibt dann als Historiendokument
   * erhalten.
   */
  readOnly?: boolean;
}

export const PMAutomationsTable: React.FC<IProps> = ({
  processManager,
  automations,
  setAutomations,
  legalClients,
  legalPhases,
  legalReqStates,
  setIsExpantedAutomations,
  triggerFilter,
  defaultTrigger,
  readOnly = false,
}) => {
  const { isLoadingManagerAutomationTypes, processManagerAutomationTypes } =
    useGetProcessManagerAutomationTypes();

  if (isLoadingManagerAutomationTypes) {
    return <CircularProgress />;
  }

  if (!processManagerAutomationTypes) {
    return <>Fehler!</>;
  }

  // Wenn ein triggerFilter gesetzt ist, nur passende Automationen anzeigen.
  // Automationen ohne ``data.Trigger`` zaehlen zum Default „balance-change"
  // (rueckwaertskompatibel mit Bestandsdaten).
  const visibleAutomations =
    triggerFilter === undefined
      ? automations
      : automations.filter(a => {
          const t = (a.data as any)?.Trigger ?? 'balance-change';
          return t === triggerFilter;
        });

  return (
    <>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Art</TableCell>
            <TableCell>Informationen</TableCell>
            <TableCell width={90}>Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visibleAutomations.map(auto => (
            <PMAutomationsRow
              key={`pm-automation-${processManager.idProcessManager}-${auto.idProcessManagerAutomation}`}
              processManager={processManager}
              automation={auto}
              automations={automations}
              setAutomations={setAutomations}
              processManagerAutomationTypes={processManagerAutomationTypes}
              legalClients={legalClients}
              legalPhases={legalPhases}
              legalReqStates={legalReqStates}
              setIsExpantedAutomations={setIsExpantedAutomations}
              readOnly={readOnly}
            />
          ))}
          {!readOnly && (
            <TableRow
              sx={{
                [`& .${tableCellClasses.root}`]: {
                  borderBottom: 'none',
                },
              }}
            >
              <TableCell colSpan={3}>
                <Box sx={{ float: 'right' }}>
                  <i>Neue Automatisierung hinzufügen</i>
                  <AddPMAutomation
                    processManager={processManager}
                    automations={automations}
                    setAutomations={setAutomations}
                    legalClients={legalClients}
                    legalPhases={legalPhases}
                    legalReqStates={legalReqStates}
                    defaultTrigger={defaultTrigger}
                  />
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
};
